import { useState } from 'react';
import jsPDF from 'jspdf';
import { calculatePDFImageSize, urlToBase64, getImageDimensions } from '@/lib/imageUtils';
import type { Issue, Screenshot } from '@/types/reportApp';
import type { ReportStatus } from '@/types/report';
import type { PackageSummaryResponse } from '@/types/packageSummary';

/**
 * Convert a screenshot to an attachment-like format for PDF rendering
 */
async function convertScreenshotToAttachment(screenshot: Screenshot, index: number) {
  try {
    const base64 = await urlToBase64(screenshot.url);
    const dimensions = await getImageDimensions(base64);

    // Infer format from base64 prefix or default to JPEG
    const format: 'JPEG' | 'PNG' = base64.includes('data:image/png') ? 'PNG' : 'JPEG';

    // Create a descriptive filename
    const timestampPart = screenshot.timestamp || screenshot.timestampSeconds?.toString() || '';
    const filename = `Screenshot ${index + 1}${timestampPart ? ` (${timestampPart})` : ''}.${format.toLowerCase()}`;

    return {
      id: `screenshot-${screenshot.id}-${index}`,
      base64,
      format,
      width: dimensions.width,
      height: dimensions.height,
      filename,
      size: 0, // Size not critical for PDF rendering
    };
  } catch (error) {
    console.error('Failed to convert screenshot:', error);
    return null;
  }
}

/**
 * Format duration in seconds to MM:SS
 */
function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format boolean values as Yes/No text
 */
function formatBoolean(value: boolean | null): string {
  if (value === null) return '—';
  return value ? 'Yes' : 'No';
}

/**
 * Format nullable string values
 */
function formatNullable(value: string | number | null): string {
  if (value === null) return '—';
  return String(value);
}

/**
 * Render package summary section in PDF
 */
function renderPackageSummary(
  doc: jsPDF,
  packageSummary: PackageSummaryResponse,
  yPos: number,
  margin: number,
  contentWidth: number,
  pageWidth: number
): number {
  // Section header
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(20, 50, 100);
  doc.text(`Package Summary (${packageSummary.master_files.length} Master File${packageSummary.master_files.length !== 1 ? 's' : ''})`, margin, yPos);
  yPos += 10;

  // Render each master file
  packageSummary.master_files.forEach((masterFile, fileIndex) => {
    // Check for page break before file header
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    // Add spacing between files
    if (fileIndex > 0) {
      yPos += 6;
    }

    // File header with background
    doc.setFillColor(248, 250, 252);
    doc.rect(margin - 5, yPos - 4, contentWidth + 10, 10, 'F');
    doc.setDrawColor(20, 50, 100);
    doc.setLineWidth(0.5);
    doc.rect(margin - 5, yPos - 4, contentWidth + 10, 10, 'S');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(20, 50, 100);
    doc.text(masterFile.filename, margin, yPos);
    yPos += 6;

    // File path
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Path: ${masterFile.file_path}`, margin, yPos);
    yPos += 8;

    // 3-column layout for metadata
    const colWidth = contentWidth / 3;
    const col1X = margin;
    const col2X = margin + colWidth;
    const col3X = margin + colWidth * 2;

    // Column headers
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text('Technical', col1X, yPos);
    doc.text('Audio', col2X, yPos);
    doc.text('Project Files', col3X, yPos);
    yPos += 5;

    // Technical column
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);

    let techYPos = yPos;
    doc.text(`Duration: ${formatDuration(masterFile.technical.duration)}`, col1X, techYPos);
    techYPos += 4;
    doc.text(`Resolution: ${formatNullable(masterFile.technical.resolution)}`, col1X, techYPos);
    techYPos += 4;
    doc.text(`Frame Rate: ${formatNullable(masterFile.technical.frame_rate)}`, col1X, techYPos);
    techYPos += 4;
    doc.text(`Title Safe: ${formatBoolean(masterFile.technical.title_safe_passed)}`, col1X, techYPos);

    // Audio column
    let audioYPos = yPos;
    doc.text(`Embedded Audio: ${formatBoolean(masterFile.audio.embedded_audio)}`, col2X, audioYPos);
    audioYPos += 4;
    // Only show embedded audio stereo if embedded_audio is true
    if (masterFile.audio.embedded_audio) {
      doc.text(`  Stereo: ${formatBoolean(masterFile.audio.embedded_audio_stereo)}`, col2X, audioYPos);
      audioYPos += 4;
    }
    doc.text(`MIX: ${formatBoolean(masterFile.audio.mix_present)}`, col2X, audioYPos);
    audioYPos += 4;
    // Only show mix stereo if mix_present is true
    if (masterFile.audio.mix_present) {
      doc.text(`  Stereo: ${formatBoolean(masterFile.audio.mix_stereo)}`, col2X, audioYPos);
      audioYPos += 4;
    }
    doc.text(`Music: ${formatBoolean(masterFile.audio.music_present)}`, col2X, audioYPos);
    audioYPos += 4;
    doc.text(`VO: ${formatBoolean(masterFile.audio.vo_present)}`, col2X, audioYPos);
    audioYPos += 4;
    doc.text(`SFX: ${formatBoolean(masterFile.audio.sfx_present)}`, col2X, audioYPos);

    // Project Files column
    let projYPos = yPos;
    doc.text(`After Effects: ${formatBoolean(masterFile.project_files.after_effects_present)}`, col3X, projYPos);
    projYPos += 4;
    doc.text(`Premiere Pro: ${formatBoolean(masterFile.project_files.premiere_pro_present)}`, col3X, projYPos);

    // Move yPos to the bottom of the tallest column
    const maxColumnHeight = Math.max(techYPos - yPos, audioYPos - yPos, projYPos - yPos);
    yPos += maxColumnHeight;

    // Separator line between files (except after last file)
    if (fileIndex < packageSummary.master_files.length - 1) {
      yPos += 4;
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 2;
    }
  });

  // Add extra spacing after package summary
  yPos += 4;

  return yPos;
}

export interface PDFExportOptions {
  reportId: string;
  packageName: string;
  issues: Issue[];
  reportStatus?: ReportStatus;
  packageSummary?: PackageSummaryResponse | null;
  notes?: string | null;
}

export const usePDFExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportPDF = async (options: PDFExportOptions, onSuccess?: () => void) => {
    setIsExporting(true);
    setExportError(null);

    try {
      const { reportId, packageName, issues, reportStatus, packageSummary, notes } = options;
      const activeIssues = issues.filter(i => i.status !== 'dismissed');

      const doc = new jsPDF();
      let yPos = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);

      // Header background box
      doc.setFillColor(240, 245, 250);
      doc.rect(0, 0, pageWidth, 55, 'F');

      // Title
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(20, 50, 100);
      const reportTitle = reportStatus === 'preqc' ? 'Pre-QC Report' : 'Quality Control Report';
      doc.text(reportTitle, margin, yPos);
      yPos += 12;

      // Metadata in two columns
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);

      const leftColX = margin;
      const rightColX = pageWidth / 2 + 10;

      doc.setFont('helvetica', 'bold');
      doc.text('Report ID:', leftColX, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(reportId || 'N/A', leftColX + 25, yPos);

      doc.setFont('helvetica', 'bold');
      doc.text('Package:', rightColX, yPos);
      doc.setFont('helvetica', 'normal');
      const packageText = doc.splitTextToSize(packageName || 'N/A', contentWidth / 2 - 30);
      doc.text(packageText, rightColX + 25, yPos);
      yPos += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Generated:', leftColX, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(new Date().toLocaleString(), leftColX + 25, yPos);
      yPos += 12;

      // Summary Section
      doc.setFillColor(250, 250, 250);
      doc.rect(margin, yPos, contentWidth, 30, 'F');
      yPos += 8;

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(20, 50, 100);
      doc.text('Summary', margin + 5, yPos);
      yPos += 8;

      const pdfRedCount = activeIssues.filter(i => i.flag === 'red').length;
      const pdfOrangeCount = activeIssues.filter(i => i.flag === 'orange').length;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);

      // Summary items with colored badges
      const summaryX = margin + 5;
      doc.text('Total Issues:', summaryX, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(`${activeIssues.length}`, summaryX + 50, yPos);

      doc.setFont('helvetica', 'normal');
      doc.text('Red Flags:', summaryX + 70, yPos);
      doc.setFillColor(220, 53, 69);
      doc.circle(summaryX + 92, yPos - 1.5, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(220, 53, 69);
      doc.text(`${pdfRedCount}`, summaryX + 96, yPos);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text('Orange Flags:', summaryX + 110, yPos);
      doc.setFillColor(255, 159, 64);
      doc.circle(summaryX + 137, yPos - 1.5, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 159, 64);
      doc.text(`${pdfOrangeCount}`, summaryX + 141, yPos);

      yPos += 12;

      // Reset text color
      doc.setTextColor(0, 0, 0);

      // Notes Section (if present)
      if (notes && notes.trim()) {
        yPos += 8;
        if (yPos > 200) {
          doc.addPage();
          yPos = 20;
        }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(20, 50, 100);
        doc.text('Notes', margin, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        const notesLines = doc.splitTextToSize(notes, contentWidth);
        notesLines.forEach((line: string) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, margin, yPos);
          yPos += 5;
        });
        yPos += 8;
      }

      // Package Summary Section (if available and not Pre-QC report)
      if (packageSummary && packageSummary.master_files.length > 0 && reportStatus !== 'preqc') {
        yPos += 8;
        if (yPos > 200) {
          doc.addPage();
          yPos = 20;
        }
        yPos = renderPackageSummary(doc, packageSummary, yPos, margin, contentWidth, pageWidth);
      }

      // Issues Section - Grouped by Filename
      if (activeIssues.length > 0) {
        yPos += 8;
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(20, 50, 100);
        doc.text(`Issues (${activeIssues.length})`, margin, yPos);
        yPos += 10;

        // Group issues by filename
        const issuesByFile = new Map<string, Issue[]>();
        activeIssues.forEach((issue) => {
          if (!issue.fileNames || issue.fileNames.length === 0) {
            // Issues without file names go under "General Issues"
            const generalKey = '_general';
            if (!issuesByFile.has(generalKey)) {
              issuesByFile.set(generalKey, []);
            }
            issuesByFile.get(generalKey)!.push(issue);
          } else {
            issue.fileNames.forEach((fileName) => {
              if (!issuesByFile.has(fileName)) {
                issuesByFile.set(fileName, []);
              }
              issuesByFile.get(fileName)!.push(issue);
            });
          }
        });

        // Iterate through each file and its issues
        const issueEntries = Array.from(issuesByFile.entries());
        for (let fileIndex = 0; fileIndex < issueEntries.length; fileIndex++) {
          const [fileName, fileIssues] = issueEntries[fileIndex];
          // Check for page break before filename
          if (yPos > 250) {
            doc.addPage();
            yPos = 20;
          }

          // Add spacing before filename (except first one)
          if (fileIndex > 0) {
            yPos += 6;
          }

          // Filename header with light background, border, and blue text
          const headerHeight = 10;

          // Light background for subtle separation
          doc.setFillColor(248, 250, 252);
          doc.rect(margin - 5, yPos - 4, contentWidth + 10, headerHeight, 'F');

          // Add a subtle border
          doc.setDrawColor(20, 50, 100);
          doc.setLineWidth(0.5);
          doc.rect(margin - 5, yPos - 4, contentWidth + 10, headerHeight, 'S');

          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(20, 50, 100);
          const displayFileName = fileName === '_general' ? 'General Issues' : fileName;
          const splitFileName = doc.splitTextToSize(displayFileName, contentWidth - 10);
          splitFileName.forEach((line: string) => {
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            doc.text(line, margin, yPos);
            yPos += 6;
          });
          yPos += 8;

          // Iterate through issues for this file
          for (let issueIndex = 0; issueIndex < fileIssues.length; issueIndex++) {
            const issue = fileIssues[issueIndex];
            // Check for page break
            if (yPos > 250) {
              doc.addPage();
              yPos = 20;
            }

            // Issue type (flag badge)
            const flagX = margin;
            const flagY = yPos - 3.5;
            const flagColor = issue.flag === 'red' ? [220, 53, 69] : [255, 159, 64];
            const flagBgColor = issue.flag === 'red' ? [254, 242, 242] : [255, 247, 237];

            doc.setFillColor(flagBgColor[0], flagBgColor[1], flagBgColor[2]);
            doc.roundedRect(flagX, flagY, 22, 6, 1, 1, 'F');

            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(flagColor[0], flagColor[1], flagColor[2]);
            doc.text(issue.flag === 'red' ? 'RED FLAG' : 'ORANGE', flagX + 2, yPos);

            yPos += 8;

            // Description (indented)
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(50, 50, 50);
            doc.text('Description:', margin, yPos);
            yPos += 5;

            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(40, 40, 40);
            const splitDescription = doc.splitTextToSize(issue.description, contentWidth - 10);
            splitDescription.forEach((line: string) => {
              if (yPos > 270) {
                doc.addPage();
                yPos = 20;
              }
              doc.text(line, margin + 5, yPos);
              yPos += 4.5;
            });

            // Details (if present)
            if (issue.details) {
              yPos += 1;
              doc.setFontSize(8);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(100, 100, 100);
              const splitDetails = doc.splitTextToSize(issue.details, contentWidth - 10);
              splitDetails.forEach((line: string) => {
                if (yPos > 270) {
                  doc.addPage();
                  yPos = 20;
                }
                doc.text(line, margin + 5, yPos);
                yPos += 4;
              });
            }

            yPos += 2;

            // Timestamps
            if (issue.timestamps && issue.timestamps.length > 0) {
              doc.setFontSize(8);
              doc.setFont('helvetica', 'bold');
              doc.setTextColor(50, 50, 50);
              doc.text('Timestamp:', margin, yPos);
              yPos += 4;

              doc.setFont('helvetica', 'normal');
              doc.setTextColor(100, 100, 100);
              issue.timestamps.forEach((ts) => {
                const tsText = ts.end ? `${ts.start} - ${ts.end}` : ts.start;
                doc.text(tsText, margin + 5, yPos);
                yPos += 4;
              });
            }

            yPos += 2;

            // Combine attachments and screenshots for display
            const allAttachments = [...(issue.attachments || [])];

            // Convert screenshots to attachment format
            if (issue.screenshots && issue.screenshots.length > 0) {
              for (let i = 0; i < issue.screenshots.length; i++) {
                const converted = await convertScreenshotToAttachment(issue.screenshots[i], i);
                if (converted) {
                  allAttachments.push(converted);
                }
              }
            }

            // Image attachments and screenshots
            if (allAttachments.length > 0) {
              const maxWidth = 80;
              const maxHeight = 110;

              allAttachments.forEach((attachment, idx) => {
                // Check if we need a new page
                if (yPos + 60 > 270) {
                  doc.addPage();
                  yPos = 20;
                }

                // Caption with background
                doc.setFillColor(248, 249, 250);
                doc.rect(margin + 10, yPos - 3, contentWidth - 20, 6, 'F');

                doc.setFontSize(8);
                doc.setFont('helvetica', 'italic');
                doc.setTextColor(100, 100, 100);
                doc.text(`Attachment ${idx + 1}: ${attachment.filename}`, margin + 12, yPos);
                yPos += 6;

                // Calculate proportional size
                const { width: imgW, height: imgH } = calculatePDFImageSize(
                  attachment.width,
                  attachment.height,
                  maxWidth,
                  maxHeight
                );

                // Add border around image
                doc.setDrawColor(220, 220, 220);
                doc.setLineWidth(0.3);
                doc.rect(margin + 10, yPos, imgW, imgH, 'S');

                // Add image
                try {
                  doc.addImage(
                    attachment.base64,
                    attachment.format,
                    margin + 10,
                    yPos,
                    imgW,
                    imgH
                  );
                  yPos += imgH + 6;
                } catch (error) {
                  console.error('Failed to add image to PDF:', error);
                  doc.setTextColor(220, 53, 69);
                  doc.setFont('helvetica', 'italic');
                  doc.text('[Image failed to load]', margin + 12, yPos);
                  yPos += 6;
                }

                // Reset styles
                doc.setTextColor(0, 0, 0);
                doc.setFont('helvetica', 'normal');
              });

              yPos += 2;
            }

            // Issue separator within same file (subtle light line)
            if (issueIndex < fileIssues.length - 1) {
              yPos += 5;
              doc.setDrawColor(230, 230, 230);
              doc.setLineWidth(0.2);
              doc.line(margin, yPos, pageWidth - margin, yPos);
              yPos += 5;
            }
          }

          // File separator - add extra space after last issue of this file
          yPos += 2;
        }
      } else {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(120, 120, 120);
        doc.text('No issues to export.', margin, yPos);
      }

      doc.save(`QC_Report_${new Date().getTime()}.pdf`);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('PDF export failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to export PDF';
      setExportError(errorMessage);
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  return { exportPDF, isExporting, exportError };
};
